import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import PetOrbitLogo from './PetOrbitLogo';
import { FiHome, FiCalendar, FiBox, FiSettings, FiZap, FiCamera, FiPlayCircle } from 'react-icons/fi';

const navItems = [
  { path: '/', icon: FiHome, label: 'Mission' },
  { path: '/schedule', icon: FiCalendar, label: 'Timeline' },
  { path: '/supplies', icon: FiBox, label: 'Cargo' },
  { path: '/training', icon: FiZap, label: 'Training' },
  { path: '/gallery', icon: FiCamera, label: 'Moments' },
  { path: '/demo', icon: FiPlayCircle, label: 'Demo' },
  { path: '/settings', icon: FiSettings, label: 'System' },
];

export default function Layout({ children }) {
  const location = useLocation();
  
  return (
    <div className="flex h-screen bg-[#F5EFE6] text-slate-800 font-sans overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white/60 backdrop-blur-xl border-r border-white/40 shadow-[4px_0_24px_rgba(0,71,171,0.05)] z-20">
        <div className="p-6">
          <PetOrbitLogo />
        </div>

        <nav className="flex-1 px-4 py-8 space-y-1 overflow-y-auto hide-scrollbar">
          {navItems.map((item) => (
            <NavLink 
              key={item.path} 
              to={item.path} 
              className={({ isActive }) => 
                `flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 relative group ${
                  isActive 
                    ? 'text-[#0047AB] font-semibold bg-white shadow-sm shadow-blue-100/50' 
                    : 'text-slate-500 hover:bg-white/50 hover:text-slate-700'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.div 
                      layoutId="activeNavTab"
                      className="absolute inset-0 bg-white rounded-2xl shadow-sm border border-[#0047AB]/10 z-0"
                      initial={false}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                  <SafeIcon icon={item.icon} className={`text-xl relative z-10 transition-colors ${isActive ? 'text-[#FF7F50]' : 'group-hover:text-[#0047AB]'}`} />
                  <span className="relative z-10">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <div className="bg-[#F5EFE6]/50 p-4 rounded-2xl text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] text-center">
            Made by LingLink AI and DigLab AI
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full relative overflow-y-auto pb-24 lg:pb-0 scroll-smooth">
        <div className="fixed top-[-10%] right-[-5%] w-96 h-96 bg-[#FF7F50]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="fixed bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-[#0047AB]/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex-1 max-w-6xl mx-auto w-full p-4 md:p-8 relative z-10">
          <motion.div 
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="min-h-full"
          >
            {children}
          </motion.div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-white/40 shadow-[0_-4px_24px_rgba(0,0,0,0.05)] z-50 pb-safe">
        <div className="flex justify-around items-center h-20 px-2">
          {navItems.map((item) => (
            <NavLink 
              key={item.path} 
              to={item.path} 
              className={({ isActive }) => 
                `flex flex-col items-center justify-center flex-1 h-full relative ${
                  isActive ? 'text-[#0047AB]' : 'text-slate-400'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.div 
                      layoutId="activeMobileTab"
                      className="absolute top-0 w-8 h-1 bg-[#FF7F50] rounded-b-full"
                    />
                  )}
                  <SafeIcon icon={item.icon} className={`text-xl mb-1 ${isActive ? 'text-[#FF7F50]' : ''}`} />
                  <span className="text-[9px] font-bold uppercase tracking-tighter whitespace-nowrap">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}